import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../shadcn/ui/card';
import { motion } from 'framer-motion';
import { applyPageMeta } from '@/lib/seo';
import {
  ANDROID_PLAY_STORE_URL,
  applySmartAppBanner,
  buildIosHandoffPayload,
  buildPlayStoreUrl,
  canonicalContentUrl,
  IOS_APP_STORE_URL,
  isAndroidBrowser,
  isIosBrowser,
  writeClipboardFromGesture,
} from '@/lib/installHandoff';

const COPY_FAILURE =
  "Statbox couldn't copy this link. You can still install the app, then open this page again after you sign in.";

const AppDownload = ({ openAppHref, openAppLabel = 'Open in Statbox' } = {}) => {
  const [copyNotice, setCopyNotice] = useState('');
  const [iosStoreUnlocked, setIosStoreUnlocked] = useState(false);
  const destination = useMemo(
    () => (typeof window === 'undefined' ? null : canonicalContentUrl(window.location.href)),
    [],
  );
  const iosDownloadUrl = IOS_APP_STORE_URL;
  const androidDownloadUrl = destination
    ? buildPlayStoreUrl(destination, Date.now()) || ANDROID_PLAY_STORE_URL
    : ANDROID_PLAY_STORE_URL;

  useEffect(() => {
    applyPageMeta({
      title: 'Download the Statbox App',
      description:
        'Get the Statbox app on iOS or Android to join games, view stats and highlights, and stay connected with your community.',
      path: '/app-download',
    });
    applySmartAppBanner(destination);
  }, [destination]);

  const openIosStore = (event) => {
    if (!destination || !isIosBrowser() || iosStoreUnlocked) return;
    event.preventDefault();
    const payload = buildIosHandoffPayload(destination, Date.now());
    const copied = payload ? writeClipboardFromGesture(payload) : false;
    if (!copied) {
      setCopyNotice(COPY_FAILURE);
      setIosStoreUnlocked(true);
      return;
    }
    window.location.assign(IOS_APP_STORE_URL);
  };

  const openAndroidStore = (event) => {
    if (!destination || !isAndroidBrowser()) return;
    event.preventDefault();
    const playUrl = buildPlayStoreUrl(destination, Date.now());
    if (playUrl) window.location.assign(playUrl);
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Background effects */}
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-tropical-teal/20 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full bg-crystal-sky/10 blur-3xl" />

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Card className="border-white/5 bg-white/5 backdrop-blur-xl shadow-xl">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4">
                <img src="/logo_white.png" alt="Statbox" className="h-12 mx-auto" />
              </div>
              <CardTitle className="text-2xl font-bold text-white">
                Download Statbox
              </CardTitle>
              <p className="text-white/70 mt-2">
                {destination
                  ? 'Download Statbox to open this link. After you sign in, the app continues to the same place.'
                  : openAppHref
                    ? 'Open this join link in the Statbox app'
                    : 'Get the Statbox app to continue'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <p className="text-white/80 text-sm mb-4">
                  {destination
                    ? 'If you already have Statbox, open this page in the app. Otherwise install it below and sign in to continue.'
                    : openAppHref
                      ? 'If you already have Statbox, tap Open in Statbox. Otherwise download the app below, then return to this link.'
                      : 'You need the Statbox app to open this link. Download it now to join games, communities, and access your stats and highlights.'}
                </p>
                {copyNotice ? (
                  <p className="text-amber-200 text-sm mb-4" role="status">
                    {copyNotice}
                  </p>
                ) : null}
                {destination && isIosBrowser() ? (
                  <p className="text-white/60 text-xs mb-4">
                    On iPhone, the download button copies this link so Statbox can open it after you install the app.
                  </p>
                ) : null}
                {destination && isAndroidBrowser() ? (
                  <p className="text-white/60 text-xs mb-4">
                    On Android, the Play Store install keeps this link so Statbox can open it after you sign in.
                  </p>
                ) : null}
              </div>

              <div className="space-y-4">
                {openAppHref && (
                  <a
                    href={openAppHref}
                    className="block w-full text-center rounded-xl bg-tropical-teal text-slate-900 font-semibold py-3 px-4 transition-transform hover:scale-[1.02]"
                  >
                    {openAppLabel}
                  </a>
                )}

                {/* Equal-sized App Store and Google Play badges */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                  {/* App Store Button */}
                  <a
                    href={iosDownloadUrl}
                    rel="noopener noreferrer"
                    className="inline-block transition-transform hover:scale-105"
                    aria-label="Download on the App Store"
                    onClick={openIosStore}
                  >
                    <img
                      src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1276560000"
                      alt="Download on the App Store"
                      className="h-12 w-auto object-contain"
                    />
                  </a>

                  {/* Google Play Button */}
                  <a
                    href={androidDownloadUrl}
                    rel="noopener noreferrer"
                    className="inline-block transition-transform hover:scale-105"
                    aria-label="Get it on Google Play"
                    onClick={openAndroidStore}
                  >
                    <img
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt="Get it on Google Play"
                      className="h-16 w-auto object-contain"
                    />
                  </a>
                </div>
              </div>

              <div className="text-center">
                <p className="text-white/60 text-xs">
                  Available on iOS and Android
                </p>
              </div>

              {/* App features preview */}
              <div className="space-y-3 pt-4 border-t border-white/10">
                <h3 className="text-white/90 font-semibold text-sm">What you can do with Statbox:</h3>
                <div className="space-y-2 text-xs text-white/70">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-tropical-teal rounded-full"></span>
                    <span>Watch full game replays</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-tropical-teal rounded-full"></span>
                    <span>View auto-generated highlights</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-tropical-teal rounded-full"></span>
                    <span>Track detailed performance stats</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-tropical-teal rounded-full"></span>
                    <span>Compete on leaderboards</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="text-center text-white/50 text-xs mt-6">
            © {new Date().getFullYear()} Statbox. All rights reserved.
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AppDownload;
